import React, { useCallback, useEffect, useState } from "react";
import { Controls, Header } from "components/Table";
import { Box, IconButton, List, ListItem, Paper, TablePagination, Typography } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import {
    deleteCreativeMapping,
    fetchCreativeMappings,
    getCreativeMappings,
    getCreativeMappingsProcessingState,
    toggleRowEdit,
    unsetCreativeMappingsRowEdit,
} from "store/features/creativeMappings/overivew";
import { useProcessingStatus, usePagination, useSubmitStatus } from "hooks";
import AddIcon from "@material-ui/icons/Add";
import {
    CreateNewMappingForm,
    CreativeMappingsForm,
    FiltersList,
    ListHeader,
    MappingsList,
    useListStyles,
} from "pages/CreativeMappings/components";
import { clearFilterFields } from "store/features/filters";

interface CreativeMappingsState {
    isNewMapping: boolean;
    id?: number;
    resetForm: false;
}

export const CreativeMappings = () => {
    // TODO: wrap with styled components when possible
    const classes = useListStyles();
    const dispatch = useDispatch();
    const [state, setState] = useState<CreativeMappingsState>({
        isNewMapping: false,
        resetForm: false,
    });
    const { isProcessing, hasError, isDeleting, isSubmitted } = useSelector(getCreativeMappingsProcessingState);
    const creativeMappings = useSelector(getCreativeMappings);
    const handleUnsetRowsEdit = useCallback(() => {
        dispatch(unsetCreativeMappingsRowEdit());
        setState((currentState) => ({
            ...currentState,
            isNewMapping: false,
        }));
    }, [dispatch]);
    const { page, rowsPerPage, handlePageChange, handleRowsPerPageChange } = usePagination(0, 5, handleUnsetRowsEdit);
    useProcessingStatus(isProcessing, hasError);
    useSubmitStatus({ isSubmitted, message: "New mapping has been created/updated successfully" });

    // TODO: Temporary walk-around - add handler for form reset!!
    useEffect(() => {
        if (state.isNewMapping && state.resetForm) {
            setState({
                resetForm: false,
                isNewMapping: true,
            });
        }
    }, [state.isNewMapping, state.resetForm]);

    useEffect(() => {
        dispatch(fetchCreativeMappings.request());

        return () => {
            // clear filters on unmount (in case if user will enter scheduler that has different filters)
            dispatch(clearFilterFields());
        };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const creativesOnPage = creativeMappings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

        // if there is no creatives to show on current page, go to previous page
        if (creativesOnPage.length === 0 && page !== 0) {
            handlePageChange(null, page - 1);
        }
        // eslint-disable-next-line
    }, [creativeMappings.length, handlePageChange, page, rowsPerPage]);

    const handleDeleteCreativeMapping = (id: number) => dispatch(deleteCreativeMapping.request(id));

    const handleEditCreativeMapping = (id: number) => dispatch(toggleRowEdit({ id }));

    const handleCopyCreativeMapping = (id: number) =>
        setState((currentState) => ({
            ...currentState,
            id,
            isNewMapping: true,
        }));

    const handleCreateNewCreativeMapping = () =>
        setState((currentState) => ({
            ...currentState,
            isNewMapping: true,
        }));

    const handleCancelEdit = (id: number) => () => dispatch(toggleRowEdit({ id }));

    const handleCancelCreateNewMapping = () => () =>
        setState((currentState) => ({
            ...currentState,
            isNewMapping: false,
        }));

    return (
        <>
            <Header title="Creative mappings">
                <IconButton onClick={handleCreateNewCreativeMapping} disabled={isDeleting || state.isNewMapping}>
                    <AddIcon color={isDeleting || state.isNewMapping ? "disabled" : "primary"} fontSize="small" />
                </IconButton>
            </Header>
            <Box mb={4}>
                <List disablePadding={true}>
                    <ListHeader />
                    {creativeMappings
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map(({ id, filters, creatives, isEdited }, index) => {
                            const rowIndex = page * rowsPerPage + index + 1;

                            return (
                                <React.Fragment key={id}>
                                    <ListItem className={classes.row}>
                                        <Box display="flex" mb={1} flex={1} component={Paper}>
                                            <Typography variant="subtitle1">
                                                <Box
                                                    minWidth={40}
                                                    p={1}
                                                    mt={0.5}
                                                    mb={0.5}
                                                    mr={0.5}
                                                    ml={0.5}
                                                    fontWeight="fontWeightBold">
                                                    {rowIndex}
                                                </Box>
                                            </Typography>
                                            {isEdited ? (
                                                <CreativeMappingsForm
                                                    id={id}
                                                    filters={filters}
                                                    creatives={creatives}
                                                    onCancelEdit={handleCancelEdit}
                                                />
                                            ) : (
                                                <>
                                                    <FiltersList filters={filters} />
                                                    <MappingsList creatives={creatives} />
                                                    <Box
                                                        p={2}
                                                        display="flex"
                                                        alignItems="flex-end"
                                                        flex={0.6}
                                                        flexDirection="column">
                                                        <Controls
                                                            id={id}
                                                            isProcessing={isDeleting}
                                                            hasError={hasError}
                                                            onEdit={handleEditCreativeMapping}
                                                            onDelete={handleDeleteCreativeMapping}
                                                            onCopy={handleCopyCreativeMapping}
                                                        />
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    </ListItem>
                                </React.Fragment>
                            );
                        })}
                    {state.isNewMapping && (
                        <CreateNewMappingForm onCancelEdit={handleCancelCreateNewMapping} id={state.id} />
                    )}
                </List>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={creativeMappings.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                />
            </Box>
        </>
    );
};
